import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Card, Skeleton } from '@rackd/ui';
import { format, parseISO } from 'date-fns';
import type { SignupTrend } from '../api';

interface SignupTrendsChartProps {
  data: SignupTrend[];
  loading?: boolean;
}

export function SignupTrendsChart({ data, loading }: SignupTrendsChartProps) {
  const chartData = data.map((d) => ({
    ...d,
    dateLabel: format(parseISO(d.date), 'MMM d'),
  }));

  if (loading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-40 mb-4" />
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-[var(--text)] mb-4">
        Daily Signups (Last 30 Days)
      </h3>

      {chartData.length === 0 ? (
        <div className="h-64 flex items-center justify-center">
          <p className="text-[var(--text-tertiary)]">No signup data available</p>
        </div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="signupGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent-primary)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--accent-primary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
              <XAxis
                dataKey="dateLabel"
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }}
                dy={10}
                interval="preserveStartEnd"
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }}
                dx={-10}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--background-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-md)',
                }}
                labelStyle={{ color: 'var(--text)', fontWeight: 600 }}
                itemStyle={{ color: 'var(--text-secondary)' }}
                formatter={(value: number) => [value, 'Signups']}
                labelFormatter={(label: string) => label}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="var(--accent-primary)"
                strokeWidth={2}
                fill="url(#signupGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {chartData.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--border)] grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-[var(--text)]">
              {chartData.reduce((sum, d) => sum + d.count, 0)}
            </p>
            <p className="text-sm text-[var(--text-tertiary)]">Total Signups</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[var(--text)]">
              {Math.max(...chartData.map((d) => d.count))}
            </p>
            <p className="text-sm text-[var(--text-tertiary)]">Peak Day</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[var(--text)]">
              {Math.round(chartData.reduce((sum, d) => sum + d.count, 0) / chartData.length)}
            </p>
            <p className="text-sm text-[var(--text-tertiary)]">Daily Average</p>
          </div>
        </div>
      )}
    </Card>
  );
}
